import React from 'react';
import { Shield, CheckCircle, ArrowRight, AlertTriangle, Lock, Eye, Edit, Users, TrendingUp } from 'lucide-react';
import ModernDocHeader from '../../components/documentation/ModernDocHeader';

const RolesPermissionsDocs: React.FC = () => {
  return (
    <div>
      <ModernDocHeader
        title="Roles & Permissions"
        description="Understand the 10 distinct user roles, access control levels, and permission structures to ensure data security while enabling effective team collaboration."
        icon={<Shield className="w-6 h-6" />}
        badge="Security"
        badgeColor="red"
      />

      <div className="p-8 pb-12">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 max-w-4xl">
          <div className="prose prose-blue dark:prose-invert max-w-none">

      {/* What You'll Learn */}
      <div className="not-prose bg-brandColor/10 dark:bg-brandColor/20 border border-brandColor/30 dark:border-brandColor/50 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Lock className="w-5 h-5 text-brandColor dark:text-brandColor mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-brandColor dark:text-brandColor mb-3">
              What You'll Learn
            </h3>
            <ul className="space-y-1 text-sm text-brandColor dark:text-brandColor/80">
              <li>✓ Understanding all 10 user roles in the system</li>
              <li>✓ Data access levels: All, Department, Team, Assigned, Own</li>
              <li>✓ Permission differences between roles</li>
              <li>✓ Role hierarchy and promotion paths</li>
              <li>✓ Best practices for role assignment</li>
              <li>✓ Security recommendations</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Overview</h2>
      <p>
        The CRM uses a sophisticated Role-Based Access Control (RBAC) system with 10 distinct roles, each with specific permissions and data access levels. This ensures data security while enabling appropriate collaboration across your organization.
      </p>

      <h2>Role Hierarchy</h2>
      <p>
        Roles are organized in a hierarchy from highest to lowest access. Understanding this hierarchy is crucial for proper team structure and security.
      </p>

      <div className="not-prose my-6">
        <div className="bg-gray-50 dark:bg-gray-800/50 border-2 border-gray-300 dark:border-gray-600 rounded-xl p-6">
          <div className="text-center space-y-3">
            <div className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg font-semibold text-sm shadow-md">
              1. SUPER ADMIN
            </div>
            <div className="text-gray-400">↓</div>
            <div className="inline-flex items-center px-4 py-2 bg-red-500 text-white rounded-lg font-semibold text-sm shadow-md">
              2. SYSTEM ADMIN
            </div>
            <div className="text-gray-400">↓</div>
            <div className="inline-flex items-center px-4 py-2 bg-brandColor text-white rounded-lg font-semibold text-sm shadow-md">
              3. SALES DIRECTOR
            </div>
            <div className="text-gray-400">↓</div>
            <div className="inline-flex items-center px-4 py-2 bg-brandColor text-white rounded-lg font-semibold text-sm shadow-md">
              4. SALES MANAGER
            </div>
            <div className="text-gray-400">↓</div>
            <div className="inline-flex items-center px-4 py-2 bg-brandColor text-white rounded-lg font-semibold text-sm shadow-md">
              5. TEAM LEAD
            </div>
            <div className="text-gray-400">↓</div>
            <div className="flex justify-center gap-4">
              <div className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg font-semibold text-sm shadow-md">
                6. SENIOR SALES REP
              </div>
              <div className="inline-flex items-center px-4 py-2 bg-green-500 text-white rounded-lg font-semibold text-sm shadow-md">
                7. SALES REP
              </div>
            </div>
            <div className="text-gray-400">↓</div>
            <div className="flex justify-center gap-4">
              <div className="inline-flex items-center px-4 py-2 bg-green-400 text-white rounded-lg font-semibold text-sm shadow-md">
                8. JUNIOR SALES REP
              </div>
              <div className="inline-flex items-center px-4 py-2 bg-purple-500 text-white rounded-lg font-semibold text-sm shadow-md">
                9. SUPPORT MANAGER
              </div>
            </div>
            <div className="text-gray-400">↓</div>
            <div className="inline-flex items-center px-4 py-2 bg-gray-500 text-white rounded-lg font-semibold text-sm shadow-md">
              10. READ ONLY
            </div>
          </div>
        </div>
      </div>

      <h2>Data Access Levels</h2>
      <p>
        Each role is assigned a data access level that determines which leads and activities they can view:
      </p>

      <div className="not-prose grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
        <div className="p-4 bg-red-50 dark:bg-red-900/10 border-2 border-red-200 dark:border-red-800 rounded-lg">
          <h4 className="font-bold text-red-900 dark:text-red-200 mb-2">ALL Data Access</h4>
          <p className="text-sm text-red-700 dark:text-red-300 mb-2">Can see and manage all data across the entire organization</p>
          <p className="text-xs text-red-600 dark:text-red-400"><strong>Roles:</strong> Super Admin, System Admin, Sales Director</p>
        </div>

        <div className="p-4 bg-brandColor/10 dark:bg-brandColor/20 border-2 border-brandColor/30 dark:border-brandColor/50 rounded-lg">
          <h4 className="font-bold text-brandColor dark:text-brandColor mb-2">DEPARTMENT Access</h4>
          <p className="text-sm text-brandColor dark:text-brandColor/80 mb-2">Can see data from users within their department</p>
          <p className="text-xs text-brandColor dark:text-brandColor/70"><strong>Roles:</strong> Sales Manager</p>
        </div>

        <div className="p-4 bg-brandColor/10 dark:bg-brandColor/20 border-2 border-brandColor/30 dark:border-brandColor/50 rounded-lg">
          <h4 className="font-bold text-brandColor dark:text-brandColor mb-2">TEAM Access</h4>
          <p className="text-sm text-brandColor dark:text-brandColor/80 mb-2">Can see data from users within their team</p>
          <p className="text-xs text-brandColor dark:text-brandColor/70"><strong>Roles:</strong> Team Lead, Support Manager</p>
        </div>

        <div className="p-4 bg-green-50 dark:bg-green-900/10 border-2 border-green-200 dark:border-green-800 rounded-lg">
          <h4 className="font-bold text-green-900 dark:text-green-200 mb-2">ASSIGNED Access</h4>
          <p className="text-sm text-green-700 dark:text-green-300 mb-2">Can only see leads that are explicitly assigned to them</p>
          <p className="text-xs text-green-600 dark:text-green-400"><strong>Roles:</strong> Senior Sales Rep, Sales Rep</p>
        </div>

        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 border-2 border-gray-200 dark:border-gray-700 rounded-lg md:col-span-2">
          <h4 className="font-bold text-gray-900 dark:text-gray-200 mb-2">OWN Access</h4>
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">Can only see data they personally created or own</p>
          <p className="text-xs text-gray-600 dark:text-gray-400"><strong>Roles:</strong> Junior Sales Rep, Read Only</p>
        </div>
      </div>

      <h2>Detailed Role Descriptions</h2>

      {/* Administrative Roles */}
      <h3>Administrative Roles</h3>

      <div className="not-prose space-y-4 my-6">
        <div className="border-2 border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 rounded-lg p-5">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-red-600 rounded-xl text-white">
              <Shield className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h4 className="text-xl font-bold text-red-900 dark:text-red-200 mb-2">1. Super Administrator</h4>
              <p className="text-red-700 dark:text-red-300 mb-3 font-medium">
                Full system access - reserved for business owners or C-level executives
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-red-800 dark:text-red-300">
                <div><strong>Data Access:</strong> ALL</div>
                <div><strong>Create Activities:</strong> Yes</div>
                <div><strong>Edit Activities:</strong> All activities</div>
                <div><strong>Delete Activities:</strong> All activities</div>
                <div><strong>Manage Users:</strong> Full control</div>
                <div><strong>Manage Settings:</strong> All settings</div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-2 border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 rounded-lg p-5">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-red-500 rounded-xl text-white">
              <Shield className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h4 className="text-xl font-bold text-red-900 dark:text-red-200 mb-2">2. System Administrator</h4>
              <p className="text-red-700 dark:text-red-300 mb-3 font-medium">
                Technical administration - typically for IT staff maintaining system integrations
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-red-800 dark:text-red-300">
                <div><strong>Data Access:</strong> ALL</div>
                <div><strong>Create Activities:</strong> Yes</div>
                <div><strong>Edit Activities:</strong> All activities</div>
                <div><strong>Delete Activities:</strong> All activities</div>
                <div><strong>Manage Users:</strong> Full control</div>
                <div><strong>Manage Settings:</strong> All technical settings</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sales Leadership Roles */}
      <h3>Sales Leadership Roles</h3>

      <div className="not-prose space-y-4 my-6">
        <div className="border-2 border-brandColor/30 dark:border-brandColor/50 bg-brandColor/10 dark:bg-brandColor/20 rounded-lg p-5">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-brandColor rounded-xl text-white">
              <TrendingUp className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h4 className="text-xl font-bold text-brandColor dark:text-brandColor mb-2">3. Sales Director</h4>
              <p className="text-brandColor dark:text-brandColor/80 mb-3 font-medium">
                Oversees all sales operations - reports and analytics for entire sales organization
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-brandColor dark:text-brandColor/80">
                <div><strong>Data Access:</strong> ALL</div>
                <div><strong>Create Activities:</strong> Yes</div>
                <div><strong>Edit Activities:</strong> All activities</div>
                <div><strong>Delete Activities:</strong> All activities</div>
                <div><strong>Manage Users:</strong> Create, edit (no delete)</div>
                <div><strong>View Reports:</strong> All reports</div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-2 border-brandColor/30 dark:border-brandColor/50 bg-brandColor/10 dark:bg-brandColor/20 rounded-lg p-5">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-brandColor rounded-xl text-white">
              <Users className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h4 className="text-xl font-bold text-brandColor dark:text-brandColor mb-2">4. Sales Manager</h4>
              <p className="text-brandColor dark:text-brandColor/80 mb-3 font-medium">
                Manages sales team and strategies - sees department-level data
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-brandColor dark:text-brandColor/80">
                <div><strong>Data Access:</strong> DEPARTMENT</div>
                <div><strong>Create Activities:</strong> Yes</div>
                <div><strong>Edit Activities:</strong> Department activities</div>
                <div><strong>Delete Activities:</strong> Own activities</div>
                <div><strong>Manage Users:</strong> View department users</div>
                <div><strong>View Reports:</strong> Department reports</div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-2 border-brandColor/30 dark:border-brandColor/50 bg-brandColor/10 dark:bg-brandColor/20 rounded-lg p-5">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-brandColor rounded-xl text-white">
              <Users className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h4 className="text-xl font-bold text-brandColor dark:text-brandColor mb-2">5. Team Lead</h4>
              <p className="text-brandColor dark:text-brandColor/80 mb-3 font-medium">
                Leads a sales team - manages and coaches team members
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-brandColor dark:text-brandColor/80">
                <div><strong>Data Access:</strong> TEAM</div>
                <div><strong>Create Activities:</strong> Yes</div>
                <div><strong>Edit Activities:</strong> Team activities</div>
                <div><strong>Delete Activities:</strong> Own activities</div>
                <div><strong>Manage Users:</strong> View team members</div>
                <div><strong>View Reports:</strong> Team reports</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sales Representative Roles */}
      <h3>Sales Representative Roles</h3>

      <div className="not-prose space-y-4 my-6">
        <div className="border-2 border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 rounded-lg p-5">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-green-600 rounded-xl text-white">
              <Edit className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h4 className="text-xl font-bold text-green-900 dark:text-green-200 mb-2">6. Senior Sales Representative</h4>
              <p className="text-green-700 dark:text-green-300 mb-3 font-medium">
                Experienced sales professional - handles complex deals
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-green-800 dark:text-green-300">
                <div><strong>Data Access:</strong> ASSIGNED</div>
                <div><strong>Create Activities:</strong> Yes</div>
                <div><strong>Edit Activities:</strong> Assigned activities</div>
                <div><strong>Delete Activities:</strong> Own activities</div>
                <div><strong>Manage Users:</strong> No access</div>
                <div><strong>View Reports:</strong> Personal reports</div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-2 border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 rounded-lg p-5">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-green-500 rounded-xl text-white">
              <Edit className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h4 className="text-xl font-bold text-green-900 dark:text-green-200 mb-2">7. Sales Representative</h4>
              <p className="text-green-700 dark:text-green-300 mb-3 font-medium">
                General sales representative - individual contributor
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-green-800 dark:text-green-300">
                <div><strong>Data Access:</strong> ASSIGNED</div>
                <div><strong>Create Activities:</strong> Yes</div>
                <div><strong>Edit Activities:</strong> Assigned activities</div>
                <div><strong>Delete Activities:</strong> Own activities</div>
                <div><strong>Manage Users:</strong> No access</div>
                <div><strong>View Reports:</strong> Personal reports</div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-2 border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 rounded-lg p-5">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-green-400 rounded-xl text-white">
              <Edit className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h4 className="text-xl font-bold text-green-900 dark:text-green-200 mb-2">8. Junior Sales Representative</h4>
              <p className="text-green-700 dark:text-green-300 mb-3 font-medium">
                Entry-level sales position - learning and development phase
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-green-800 dark:text-green-300">
                <div><strong>Data Access:</strong> OWN</div>
                <div><strong>Create Activities:</strong> Yes</div>
                <div><strong>Edit Activities:</strong> Own activities only</div>
                <div><strong>Delete Activities:</strong> Own activities only</div>
                <div><strong>Manage Users:</strong> No access</div>
                <div><strong>View Reports:</strong> Personal reports only</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Support & Special Roles */}
      <h3>Support & Special Roles</h3>

      <div className="not-prose space-y-4 my-6">
        <div className="border-2 border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/20 rounded-lg p-5">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-purple-500 rounded-xl text-white">
              <Users className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h4 className="text-xl font-bold text-purple-900 dark:text-purple-200 mb-2">9. Support Manager</h4>
              <p className="text-purple-700 dark:text-purple-300 mb-3 font-medium">
                Manages support operations - oversees support team activities
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-purple-800 dark:text-purple-300">
                <div><strong>Data Access:</strong> TEAM</div>
                <div><strong>Create Activities:</strong> Yes</div>
                <div><strong>Edit Activities:</strong> Team activities</div>
                <div><strong>Delete Activities:</strong> Own activities</div>
                <div><strong>Manage Users:</strong> View team members</div>
                <div><strong>View Reports:</strong> Team reports</div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-2 border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-5">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-gray-500 rounded-xl text-white">
              <Eye className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h4 className="text-xl font-bold text-gray-900 dark:text-gray-200 mb-2">10. Read Only</h4>
              <p className="text-gray-700 dark:text-gray-300 mb-3 font-medium">
                View-only access - typically for auditors or external consultants
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-800 dark:text-gray-300">
                <div><strong>Data Access:</strong> OWN</div>
                <div><strong>Create Activities:</strong> No</div>
                <div><strong>Edit Activities:</strong> No</div>
                <div><strong>Delete Activities:</strong> No</div>
                <div><strong>Manage Users:</strong> No access</div>
                <div><strong>View Reports:</strong> Limited to assigned data</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <h2>Role Assignment Best Practices</h2>

      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 my-8">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-3">
              Security Best Practices
            </h3>
            <ul className="space-y-2 text-sm text-green-800 dark:text-green-300">
              <li>✓ <strong>Principle of Least Privilege:</strong> Assign the minimum role required for the job function</li>
              <li>✓ <strong>Limit Administrative Roles:</strong> Maximum 1-2 Super Admins, 2-3 System Admins per organization</li>
              <li>✓ <strong>Regular Reviews:</strong> Audit user roles quarterly and adjust as responsibilities change</li>
              <li>✓ <strong>Immediate Revocation:</strong> Deactivate or demote users when they change roles or leave</li>
              <li>✓ <strong>No Role Sharing:</strong> One user = one account, never share login credentials</li>
              <li>✓ <strong>Document Changes:</strong> Log who changed roles and why for compliance</li>
              <li>✓ <strong>Training:</strong> Ensure users understand their role's capabilities and limitations</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Common Scenarios</h2>

      <h3>Scenario 1: New Entry-Level Sales Rep</h3>
      <p><strong>Recommendation:</strong> Start with <strong>Junior Sales Representative</strong></p>
      <ul>
        <li>Limited access to own data only (data_access: own)</li>
        <li>Can create and manage their own activities</li>
        <li>Cannot see team or company-wide data</li>
        <li>Reduces risk while they learn the system</li>
        <li>Promote to Sales Rep after 3-6 months of good performance</li>
      </ul>

      <h3>Scenario 2: Experienced Sales Professional</h3>
      <p><strong>Recommendation:</strong> Assign <strong>Senior Sales Representative</strong></p>
      <ul>
        <li>Access to all assigned leads (data_access: assigned)</li>
        <li>Can manage multiple complex deals</li>
        <li>Personal performance reports</li>
        <li>Can be promoted to Team Lead if managing others</li>
      </ul>

      <h3>Scenario 3: Team Manager with 5-10 Reports</h3>
      <p><strong>Recommendation:</strong> Use <strong>Team Lead</strong> role</p>
      <ul>
        <li>Can see all team members' data (data_access: team)</li>
        <li>Coach and mentor team effectively</li>
        <li>Manage team activities and schedules</li>
        <li>View team performance reports</li>
        <li>Cannot access other teams' data for privacy</li>
      </ul>

      <h3>Scenario 4: Department Head</h3>
      <p><strong>Recommendation:</strong> Assign <strong>Sales Manager</strong> role</p>
      <ul>
        <li>Oversees multiple teams in the department (data_access: department)</li>
        <li>Can view and analyze department-wide performance</li>
        <li>Manage department settings and strategies</li>
        <li>Cannot access other departments' data</li>
      </ul>

      <div className="not-prose bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 my-6">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-yellow-800 dark:text-yellow-300 mb-2">
              <strong>Important: Data Access Scope</strong>
            </p>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              Users can only see data within their data access scope. For example, a Team Lead with "team" access cannot see activities or leads from other teams, even if they navigate directly to those records via URL.
            </p>
          </div>
        </div>
      </div>

      <h2>Next Steps</h2>

      <div className="not-prose space-y-3 mt-6">
        <a
          href="/docs/user-management"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-brandColor dark:hover:border-brandColor transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">User Management</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Learn how to create users and assign roles
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>

        <a
          href="/settings/users"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-brandColor dark:hover:border-brandColor transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Manage Users & Roles</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Go to user management settings
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>
      </div>

      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 mt-8">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-2">
              You're Ready to Assign Roles!
            </h3>
            <p className="text-green-800 dark:text-green-300 mb-3">
              You now understand the 10 roles, data access levels, and best practices for role assignment. Remember to follow the principle of least privilege and regularly review user permissions.
            </p>
            <a
              href="/settings/users"
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
            >
              <Shield className="w-4 h-4 mr-2" />
              Manage User Roles
            </a>
          </div>
        </div>
      </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default RolesPermissionsDocs;
